import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import React, { useContext, useEffect, useState } from "react"
import api from "../../api2"
import { ReservationProps } from "../../interfaces"
import Modal from "../ui/Modal"
import Spinner from "../ui/Spinner"
import * as Sentry from "@sentry/react"
import Button from "../ui/Button"
import StripeCardForm from "./PaymentModal/StripeCardForm"
import PaymentLinkForm from "./PaymentModal/PaymentLinkForm"
import { AdminContext } from "~/contexts/AdminContext"
import { Transition } from "@headlessui/react"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import IGroupedReservation from "../../interfaces/IGroupedReservation"
import AdminAddOns from "./PaymentModal/AdminAddOns"
import IAddOn from "../../interfaces/IAddOn"
import PaymentTypeSelector from "./PaymentModal/PaymentTypeSelector"
import { useDebouncedCallback } from "use-debounce"
import classNames from "../../utils/classNames"
import IOrder from "../../interfaces/IOrder"
import { XMarkIcon } from "@heroicons/react/24/solid"
import ILineItem from "../../interfaces/ILineItem"
import { mapLineItemToParams, partialLineItemParams } from "~/api2/orders"
import OrderSummary from "../Admin/Orders/OrderSummary"
import { WifiIcon } from "@heroicons/react/24/outline"
import ePaymentType from "../../enums/ePaymentType"
import serviceRateModeForPaymentType from "../../utils/serviceRateModeForPaymentType"
import ProductModalContent from "../Admin/Settings/Product/ProductModalContent"
import IOrderPreview from "../../interfaces/IOrderPreview"
import { reservationStatus } from "~/constants"
import PartialPaymentInput from "./PaymentModal/PartialPaymentInput"
import useSetTaxFees from "~/components/Admin/Invoices/hooks/useSetTaxFees"
import { usePosModal } from "~/hooks/usePosModal"
import _ from "lodash"
import ConfirmationModal from "~/components/Reservations/ConfirmationModal"
import { PaymentMethodType } from "~/interfaces/IPaymentMethods"
import StoreSearchBar from "../Admin/Store/StoreSearchBar"
import SelectInput from "../ui/SelectInput"
import { useCookies } from "react-cookie"
import { useCamp } from "~/api2/hooks/useCamp"
import { useCampingStyles } from "~/api2/hooks/useCampingStyles"
import useSites from "~/api2/hooks/useSites"
import { useNotification } from "~/contexts/NotificationProvider"
import { useModal } from "~/contexts/ModalProvider"
import ICamper from "~/interfaces/ICamper"
import IPayment from "~/interfaces/IPayment"

export interface PosModalProps {
  open: boolean
  setOpen: (arg: boolean) => void
  onClose: () => void
  reservation?: ReservationProps
  reservations?: ReservationProps[]
  groupedReservation?: IGroupedReservation
  owedInCents: number
  onChangeIntentId: (intentId: string) => Promise<any>
  isInitializedDeposit?: boolean
  isDeposit?: boolean
  updateReservationCallback?: (reservation: { id?: string; status?: reservationStatus }) => void
}

const PosModal = ({
  open,
  setOpen,
  onClose,
  reservation,
  reservations,
  groupedReservation,
  owedInCents,
  onChangeIntentId,
  isInitializedDeposit,
}: PosModalProps) => {
  const orderId = (groupedReservation || reservation)?.order_id
  const paidInCents =
    (reservations?.length || 0 > 0
      ? (reservations as ReservationProps[]).reduce((acc, r) => acc + (r.total_paid_in_cents || 0), 0)
      : reservation?.total_paid_in_cents) || 0
  const {
    index: { data: camp = {} },
  } = useCamp()
  console.log(camp)
  const {
    index: { data: campingStyles },
  } = useCampingStyles()
  const {
    index: { data: sites },
  } = useSites()
  const { showNotification } = useNotification()
  const { openModal } = useModal()
  const { showModal, updateLocalReservations } = useContext(AdminContext)

  const campingStyle =
    campingStyles?.find((cs) => {
      const mReservations =
        groupedReservation?.reservations && groupedReservation?.reservations.length > 0
          ? groupedReservation?.reservations
          : reservations || (reservation ? [reservation] : [])
      const mSites = sites?.filter((s) =>
        mReservations
          .filter((r) => r)
          .map((r) => r.site_id)
          .includes(s.id),
      )
      return (mSites?.map((s) => s.camping_style_id) || []).includes(cs.id)
    }) || campingStyles?.find((cs) => cs.id === reservation?.selected_camping_style_id)

  const [intentId, setIntentId] = useState<string>()
  const [isPartialAmountActive, setIsPartialAmountActive] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [amountOwed, setAmountOwed] = useState(owedInCents / 100)
  const [amountPaid, setAmountPaid] = useState(paidInCents / 100)
  const [products, setProducts] = useState<IAddOn[]>([])
  const [paymentMethod, setPaymentMethod] = useState<ePaymentType>(ePaymentType.CARD_INPUT)
  const [order, setOrder] = useState<IOrder>()
  const [lineItems, setLineItems] = useState<Partial<ILineItem>[]>()
  const [orderWithFees, setOrderWithFees] = useState<IOrder | IOrderPreview>()
  const [depositOrder, setDepositOrder] = useState<IOrder>()
  const [isServiceFeeEnabled, setIsServiceFeeEnabled] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState("")
  const [intervalId, setIntervalId] = useState<number>()
  const [showCloseButton, setShowCloseButton] = useState(false)
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<PaymentMethodType>()
  const [pendingRequests, setPendingRequests] = useState(0)

  const unpaid = order ? order.unpaid_subtotal_in_cents / 100 : owedInCents / 100
  const isPartialPayment = isPartialAmountActive && !isInitializedDeposit && order && amountOwed < unpaid
  const { discountFromLineItems } = usePosModal(lineItems, camp, campingStyle)

  const [filter, setFilter] = useState("")
  const [cookies, setCookie] = useCookies(["pos_sort_option"])
  const [sortOption, setSortOption] = useState<string>(cookies["pos_sort_option"] || "alphabetical-asc")

  const sortOptions = [
    { label: "Alphabetical (A-Z)", value: "alphabetical-asc" },
    { label: "Alphabetical (Z-A)", value: "alphabetical-desc" },
    { label: "Price (Low-High)", value: "price-asc" },
    { label: "Price (High-Low)", value: "price-desc" },
  ]

  const sortProducts = (addons: IAddOn[]) => {
    if (!addons) return []

    switch (sortOption) {
      case "alphabetical-asc":
        return [...addons].sort((a, b) => a.label.localeCompare(b.label))
      case "alphabetical-desc":
        return [...addons].sort((a, b) => b.label.localeCompare(a.label))
      case "price-asc":
        return [...addons].sort((a, b) => a.amount_in_cents - b.amount_in_cents)
      case "price-desc":
        return [...addons].sort((a, b) => b.amount_in_cents - a.amount_in_cents)
      default:
        return [...addons].sort((a, b) => a.label.localeCompare(b.label))
    }
  }

  const {
    taxFeeEnabled: isTaxFeeEnabled,
    creditCardFeeEnabled: isCreditCardFeeEnabled,
    setCreditCardFeeEnabled: setIsCreditCardFeeEnabled,
    setTaxFeeEnabled: setIsTaxFeeEnabled,
    TaxToggle,
  } = useSetTaxFees({
    order: order!,
    campingStyle: campingStyle,
    isStore: true,
    paymentMethod,
    isLongTerm: !!reservation?.long_term_reservation_id,
  })

  const handleChangePaymentType = (v: ePaymentType) => {
    setPaymentMethod(v)
    if (v === ePaymentType.CASH_PAID || v === ePaymentType.CASH_UNPAID) {
      setIsCreditCardFeeEnabled(false)
      setIsServiceFeeEnabled(false)
    } else {
      setIsCreditCardFeeEnabled(true)
      setIsServiceFeeEnabled(true)
    }
  }
  const handleProductClick = (selectedProduct?: IAddOn) => {
    if (window.location.pathname === "/admin/reservations") {
      const createAddonId = "create-addon"
      showModal(
        <ProductModalContent fetchProducts={fetchProducts} selectedProduct={selectedProduct} />,
        true,
        "max-w-4xl",
        {
          closeCallback: () => {},
          id: createAddonId,
        },
      )
    } else {
      openModal("generic", {
        children: <ProductModalContent fetchProducts={fetchProducts} selectedProduct={selectedProduct} />,
        className: "max-w-4xl",
        onClose: () => {},
      })
    }
  }

  useEffect(() => {
    if (!open) return
    buildOrder()
    if (isInitializedDeposit || isPartialPayment) buildDepositOrder()
    else setDepositOrder(null)
  }, [
    lineItems,
    isCreditCardFeeEnabled,
    paymentMethod,
    isTaxFeeEnabled,
    isInitializedDeposit,
    amountOwed,
    selectedPaymentMethodType,
  ])

  useEffect(() => {
    fetchProducts()
  }, [amountOwed, amountPaid, camp, sites])

  useEffect(() => {
    if (!open) return
    fetchOrder(orderId)
  }, [reservation, isInitializedDeposit, open, isPartialAmountActive])

  const handleClosed = () => {
    setPaymentStatus(null)
    setErrorMessage("")
    setLineItems([])
    setOrder(null)
    setDepositOrder(null)
    clearInterval(intervalId)
    setShowCloseButton(false)

    if (intentId && camp.has_terminal_readers) {
      api.Terminal.cancelCurrentPayment(camp.id, intentId).catch((err) => {
        console.error(err)
      })
    }
  }

  useEffect(() => {
    if (!open) handleClosed()
  }, [open])

  useEffect(() => {
    if (intentId) onChangeIntentId(intentId)
  }, [intentId])

  useEffect(() => {
    setAmountPaid(paidInCents / 100)
  }, [paidInCents])

  useEffect(() => {
    setAmountOwed(owedInCents / 100)
  }, [owedInCents])

  const fetchProducts = async (): Promise<void> => {
    api.Products.forCamp(camp.id, {
      campingStyleIds: _.uniq(sites?.map((site) => site.camping_style_id)),
    })
      .then(({ products: p }) => {
        setProducts(p)
      })
      .catch((err) => {
        console.error(err)
      })
  }

  const fetchOrder = useDebouncedCallback((orderId: string) => {
    if (!orderId) {
      setOrder(null)
      return
    }

    api.Orders.fetch(orderId, true)
      .then(({ order }: { order: IOrder }) => {
        setOrder(order)
        setIsTaxFeeEnabled(order.tax_fee_in_cents > 0)
        setIsServiceFeeEnabled(paymentMethod !== ePaymentType.CASH_PAID && paymentMethod !== ePaymentType.CASH_UNPAID)
        setLineItems(order.line_items)
      })
      .catch((err) => {
        console.error(err)
      })
  }, 100)

  const buildOrder = useDebouncedCallback(() => {
    if (!camp || (!campingStyle && !groupedReservation)) return
    setPendingRequests((prev) => prev + 1)
    fetchPreview()
      .then(({ order: { preview } }) => {
        setOrder(preview)
        setIsLoading(false)
        setPendingRequests((prev) => prev - 1)
      })
      .catch((err) => {
        console.error(err)
        Sentry.captureException(err)
        setIsLoading(false)
        setPendingRequests((prev) => prev - 1)
        //setErrorMessage('Something went wrong. Please reload the page')
      })

    // NOTE: Used to fetch the tax_rate even when it's not enabled, display on the tax toggle
    if (!isTaxFeeEnabled) {
      setPendingRequests((prev) => prev + 1)
      fetchPreview({ forceFeesEnabled: true })
        .then(({ order: { preview } }) => {
          setOrderWithFees(preview)
          setPendingRequests((prev) => prev - 1)
        })
        .catch((err) => {
          console.error(err)
          setPendingRequests((prev) => prev - 1)
        })
    }
  }, 100)

  const fetchPreview = async ({ forceFeesEnabled = false } = {}) => {
    return api.Orders.createPreview({
      order_id: orderId,
      camp_id: camp.id,
      camper_id: reservation?.camper_id || order?.camper_id,
      camping_style_id: campingStyle?.id,
      is_credit_card_fee_enabled: forceFeesEnabled || isCreditCardFeeEnabled,
      is_service_fee_enabled: forceFeesEnabled || isServiceFeeEnabled,
      service_rate_mode: serviceRateModeForPaymentType(paymentMethod),
      discount_slug: discountFromLineItems?.slug,
      line_items_attributes: lineItems?.map((li) => mapLineItemToParams(li, forceFeesEnabled || isTaxFeeEnabled)) || [],
      selected_payment_method: selectedPaymentMethodType,
    })
  }

  const buildDepositOrder = useDebouncedCallback(async () => {
    if ((!camp || !campingStyle) && !groupedReservation) return

    api.Orders.createPreview({
      order_id: orderId,
      camp_id: camp.id,
      camping_style_id: !groupedReservation ? campingStyle.id : undefined,
      is_credit_card_fee_enabled: isCreditCardFeeEnabled,
      is_service_fee_enabled: isServiceFeeEnabled,
      service_rate_mode: serviceRateModeForPaymentType(paymentMethod),
      discount_slug: discountFromLineItems?.slug,
      line_items_attributes: partialLineItemParams(
        amountOwed * 100.0,
        isPartialPayment ? isTaxFeeEnabled : false,
        reservation?.id,
        isInitializedDeposit,
      ),
      is_partial_payment: isPartialPayment,
    })
      .then(({ order: { preview } }) => {
        setDepositOrder(preview)
      })
      .catch((err) => {
        console.error(err)
      })
  }, 100)

  const handleClickTakeCashPayment = async (e) => {
    setPendingRequests((prev) => prev + 1)
    api.Orders.createPayment(
      {
        order_id: orderId,
        camp_id: camp.id,
        camping_style_id: campingStyle?.id,
        is_credit_card_fee_enabled: isCreditCardFeeEnabled,
        discount_slug: discountFromLineItems?.slug,
        line_items_attributes: lineItems?.map((li) => mapLineItemToParams(li, isTaxFeeEnabled)) || [],
        partial_line_items_attributes: depositOrder?.line_items.map(
          (li) => mapLineItemToParams(li, isTaxFeeEnabled), // NOTE: assuming you cannot make a deposit from this modal anymore
        ),
      },
      {
        isForGroup: !!groupedReservation,
      },
    )
      .then((resp) => {
        setPendingRequests((prev) => prev - 1)
        handleSuccess()
        setDepositOrder(null)
        onClose()
      })
      .catch((err) => {
        setPendingRequests((prev) => prev - 1)
        console.error(err)
        showNotification({ type: "error", title: err.message })
      })
  }

  const shownProducts = sortProducts(
    products?.filter(
      (addon) =>
        !["always-applied", "hidden"].includes(addon.kind) &&
        addon.addable_camping_style_id === null &&
        (addon.label.toLowerCase().includes(filter.toLowerCase()) ||
          (addon.sku && addon.sku.toLowerCase().includes(filter.toLowerCase())) ||
          (addon.description && addon.description.toLowerCase().includes(filter.toLowerCase()))),
    ),
  )

  const summaryOrder = isInitializedDeposit || isPartialPayment ? depositOrder : order

  const isShowingProducts = !groupedReservation

  const handleSuccess = () => {
    setPaymentStatus("success")
  }

  const taxRateLabel = campingStyle?.tax_rate
    ? campingStyle?.tax_rate_label
    : camp?.tax_rate
      ? camp?.tax_rate_label
      : "Tax"

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="z-40 relative"
      maxWidth={isShowingProducts ? "max-w-7xl" : "max-w-5xl"}
    >
      <Sentry.ErrorBoundary
        showDialog
        fallback={
          <div className="w-full my-24 flex items-center justify-center">
            <div className="text-center flex flex-col items-center">
              <div className="rounded-full w-min bg-red-100 p-4 mb-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
              </div>
              <div className="text-lg font-semibold">Something went wrong</div>
              <div className="mb-4">Please reload the page and try again.</div>
              <div className="w-full">
                <Button onClick={() => window.location.reload()} variant="white">
                  Reload page
                </Button>
              </div>
            </div>
          </div>
        }
      >
        <>
          <div className="text-xl font-bold mb-4">Take payment</div>
          <div className="flex w-full flex-col">
            <div className="flex flex-col md:flex-row justify-between gap-6">
              <div className="flex-grow flex-1 flex flex-col">
                {isShowingProducts ? (
                  <>
                    <div className="pt-4 border-2 md:border-none border-white md:pt-0 flex-wrap flex items-center pb-1 justify-between w-full bg-white border-b z-10 sticky -top-2 gap-x-2">
                      <div className="flex gap-x-2 items-center flex-grow">
                        <StoreSearchBar setFilter={setFilter} />
                        <SelectInput
                          className="w-48"
                          options={sortOptions}
                          selected={sortOptions.find((option) => option.value === sortOption) || sortOptions[0]}
                          setSelected={(option) => {
                            setSortOption(option.value)
                            setCookie("pos_sort_option", option.value, { path: "/" })
                          }}
                          noIcon={true}
                        />
                      </div>
                    </div>
                    <div className="overflow-y-scroll overflow-x-hidden h-[575px] md:h-[86vh]">
                      <AdminAddOns
                        selected_interval={campingStyle?.booking_intervals.find(
                          (interval) => interval.id === reservation?.selected_booking_interval_id,
                        )}
                        addOns={shownProducts}
                        primaryColor={camp?.primary_color}
                        lineItems={lineItems || []}
                        setLineItems={setLineItems as (v: Array<Partial<ILineItem>>) => void}
                        handleProductClick={handleProductClick}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex-grow block" />
                )}
              </div>
              {isLoading ? (
                <Transition
                  show={isLoading}
                  enter="transition-opacity ease-out duration-75"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="transition-opacity ease-in duration-75"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="z-10 absolute w-full h-full bg-white opacity-50 flex items-center justify-center">
                    <Spinner size={6} />
                  </div>
                </Transition>
              ) : (
                <div className="flex flex-col w-full flex-shrink-0 max-w-mdlg">
                  <div className={classNames(isShowingProducts ? "md:h-56 overflow-y-auto" : "h-24")}>
                    {order?.line_items?.map((line_item, index) => (
                      <div key={index} className="rounded-md border border-gray-200 px-4 py-2 mb-1.5">
                        <div className="mb-1 flex justify-between items-center">
                          <div className="flex flex-col">
                            <label htmlFor="name" className="flex items-center text-base text-gray-900">
                              <span>{line_item.label}</span>
                              <XMarkIcon className="w-4 h-4 inline mx-1 mt-0.5" />
                              <span>{line_item.quantity}</span>
                            </label>
                          </div>
                          <span className="text-gray-900">
                            <span>{formatMoneyFromCents(line_item.total_amount_in_cents, camp.currency)}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="">
                    <div className="flex flex-col justify-end mb-4">
                      <div className="flex justify-between items-center">
                        {order?.unpaid_total_in_cents > 0 && (
                          <PartialPaymentInput
                            isActive={isPartialAmountActive}
                            setIsActive={setIsPartialAmountActive}
                            isDeposit={isInitializedDeposit || false}
                            amountOwed={amountOwed}
                            setAmountOwed={setAmountOwed}
                            maxValue={order ? (order.unpaid_subtotal_in_cents - 1) / 100 : owedInCents / 100}
                          />
                        )}
                        {TaxToggle}
                      </div>
                      <div className="w-full">
                        <OrderSummary
                          order={summaryOrder as IOrder}
                          camp={camp}
                          isPreview
                          isDeposit={isInitializedDeposit}
                          showProcessingFeesLabel={selectedPaymentMethodType === "us_bank_account"}
                          isLoading={!summaryOrder}
                          isRecurring={!!reservation?.long_term_reservation_id}
                          taxRateLabel={taxRateLabel}
                        />
                      </div>
                    </div>
                    <div className="">
                      <>
                        <div className="">
                          <div className="mb-2 flex justify-between items-center">
                            <label htmlFor="name" className="inline font-bold text-md text-gray-900">
                              Payment method
                            </label>
                          </div>
                          <PaymentTypeSelector
                            options={[ePaymentType.CARD_INPUT, ePaymentType.LINK, ePaymentType.CASH_PAID]}
                            selectedOption={paymentMethod}
                            onChange={handleChangePaymentType}
                          />
                          {paymentMethod === ePaymentType.CASH_PAID ? (
                            <div className="flex justify-end items-center mt-6">
                              <Button
                                disabled={!lineItems?.length || pendingRequests > 0}
                                variant="blue"
                                onClick={handleClickTakeCashPayment}
                              >
                                Take offline payment
                              </Button>
                            </div>
                          ) : paymentMethod === ePaymentType.LINK ? (
                            <PaymentLinkForm
                              order={order as IOrder}
                              email={(reservation || groupedReservation)?.camper?.email || ""}
                              campingStyleId={campingStyle?.id}
                              onSuccess={handleSuccess}
                              isTaxFeeEnabled={isTaxFeeEnabled}
                              isCreditCardFeeEnabled={isCreditCardFeeEnabled}
                              discountSlug={discountFromLineItems?.slug}
                              lineItems={lineItems || []}
                              depositLineItems={depositOrder?.line_items || []}
                              pendingRequests={pendingRequests}
                            />
                          ) : (
                            <StripeCardForm
                              ctaText={`Charge ${formatMoneyFromCents(
                                summaryOrder?.unpaid_total_in_cents,
                                camp?.currency,
                              )}`}
                              setPaymentIntentId={setIntentId}
                              paymentIntentId={intentId}
                              onSuccess={handleSuccess}
                              order={order as IOrder}
                              camperId={reservation?.camper_id || order?.camper_id}
                              isCamperPresent={!!reservation?.camper_id}
                              camper={reservation?.camper as ICamper}
                              campingStyleId={campingStyle?.id}
                              isTaxFeeEnabled={isTaxFeeEnabled}
                              isCreditCardFeeEnabled={isCreditCardFeeEnabled}
                              discountSlug={discountFromLineItems?.slug}
                              lineItems={lineItems || []}
                              depositLineItems={depositOrder?.line_items || []}
                              paymentMethod={paymentMethod}
                              setPaymentMethod={setPaymentMethod}
                              errorMessage={errorMessage}
                              setErrorMessage={setErrorMessage}
                              setPaymentStatus={setPaymentStatus}
                              setIntervalId={setIntervalId}
                              setShowCloseButton={setShowCloseButton}
                              minTotalInCents={50}
                              isDisabledButton={!!reservation?.deleted_at || !lineItems?.length || pendingRequests > 0}
                              isOrderRecurring={!!reservation?.long_term_reservation_id}
                              isEnabledAutoCharging={reservation?.long_term_reservation?.is_enabled_auto_charging}
                              selectedPaymentMethodType={selectedPaymentMethodType}
                              setSelectedPaymentMethodType={setSelectedPaymentMethodType}
                            />
                          )}
                        </div>
                      </>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {(paymentStatus != null || showCloseButton) && (
            <ConfirmationModal
              paymentStatus={paymentStatus}
              setPaymentStatus={setPaymentStatus}
              paymentMethod={paymentMethod}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              onCtaClick={() => setOpen(false)}
              showCloseButton={showCloseButton}
              setShowCloseButton={setShowCloseButton}
              camp={camp}
              paymentIntentId={intentId}
              intervalId={intervalId}
            />
          )}
        </>
      </Sentry.ErrorBoundary>
    </Modal>
  )
}

export default PosModal
